import { useEffect, useState } from 'react';
import { ChevronsUpDown, Check } from 'lucide-react';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { GAMES } from '@/config/games';
import styles from './GameSwitcher.module.scss';

/**
 * Sélecteur de serveur (Paladium / NationsGlory / Recube), affiché dans la barre du haut.
 * On ne montre QUE les projets où l'utilisateur a au moins une permission (admin = tous).
 */
export function GameSwitcher() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const setActiveGame = useGameStore((s) => s.setActiveGame);
  const hasProject = useAuthStore((s) => s.hasProject);
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);

  const visibleGames = GAMES.filter((g) => hasProject(g.id));

  // Si le projet actif n'est pas (ou plus) accessible, basculer sur le premier accessible.
  useEffect(() => {
    if (visibleGames.length > 0 && !visibleGames.some((g) => g.id === activeGameId)) {
      setActiveGame(visibleGames[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeGameId]);

  // Rien à sélectionner (aucun projet accessible) → pas de sélecteur.
  if (visibleGames.length === 0) return null;
  const active = visibleGames.find((g) => g.id === activeGameId) ?? visibleGames[0]!;
  // Un seul projet accessible : badge statique, sans menu déroulant.
  const singleProject = visibleGames.length === 1;

  return (
    <div className={styles.gameSwitcher}>
      <button
        type="button"
        className={styles.button}
        onClick={() => { if (!singleProject) setOpen((o) => !o); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={singleProject}
      >
        <span className={styles.badge} style={{ backgroundColor: active.accent.light, color: active.accent.primary }}>{active.shortName}</span>
        <span className={styles.name}>{active.name}</span>
        {!singleProject && <ChevronsUpDown size={16} className={styles.chevron} />}
      </button>

      {open && !singleProject && (
        <>
          <div className={styles.overlay} onClick={() => setOpen(false)} />
          <ul className={styles.menu} role="listbox">
            {visibleGames.map((game) => (
              <li key={game.id}>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    setActiveGame(game.id);
                    setOpen(false);
                  }}
                  role="option"
                  aria-selected={game.id === activeGameId}
                >
                  <span className={styles.badge} style={{ backgroundColor: game.accent.light, color: game.accent.primary }}>{game.shortName}</span>
                  <span className={styles.name}>{game.name}</span>
                  {game.id === activeGameId && <Check size={16} className={styles.check} />}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
